import Table from "@/common/elements/Table";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "../InmateDetails/styles";
import Button from "@/common/elements/Button";
import { PlusOutlined } from "@ant-design/icons";
import { StyledFlex } from "@/common/styles";
import CustomModal from "../CustomModal";
import BailPaymentDetailForm from "./BailPaymentDetailForm";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidV4 } from "uuid";
import { Form, Popconfirm } from "antd";
import {
  setBookings,
  setDeleteAPis,
  setLastBookingsFromAPI,
} from "@/slices/bookingsSlice";
import {
  getBookingsByNumberUtil,
  getRefListNameByID,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import dayjs from "dayjs";
import useDialog from "@/hooks/useDialog";
import _ from "lodash";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import { saveExtraTableRecord } from "@/util/extraColumnActions";
import notificationService from "@/util/notificationService";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const BAIL_PAYMENTS_HEADERS = [
  { key: "id", text: "ID" },
  { key: "dateAdded", text: "Date Added" },
  { key: "timeAdded", text: "Time Added" },
  { key: "paymentAmount", text: "Amount" },
  { key: "paymentType", text: "Payment Type" },
  { key: "fullName", text: "Full Name" },
  { key: "bondedBy", text: "Bonded By" },
  { key: "bondNumber", text: "Bond Number" },
];

const ChargesBailPaymentsDetails = ({
  bookingBailsData,
  bookingPayments,
  chargesData,
  startingPoint,
}) => {
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings,
  );

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: bailPaymentsHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:BookingBailPayments", BAIL_PAYMENTS_HEADERS);

  const { showDialog } = useDialog();
  const dispatch = useDispatch();
  const [singleBookingPayments, setSingleBookingPayments] = useState(null);
  const [bookingBails, setBookingBails] = useState([]);
  const [isBailModal, setIsBailModal] = useState(false);
  const [isEditVisible, setisEditVisible] = useState(false);
  const [paymentsData, setPaymentsData] = useState([]);
  const [paymentForm] = Form.useForm();
  const [isAPICall, setisAPICall] = useState(false);
  const [isEdit, setisEdit] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const fullSearch = useSelector((state) => state.fullSearch);

  // BookingBailPaymentsExtra field keys/type map/current PK, reported up
  // from BailPaymentDetailForm — see the onExtraMetaChange prop passed to
  // it below. Read at save time, not a render input, so a ref is enough.
  const bailPaymentExtraMetaRef = useRef({
    fieldKeys: [],
    typeMap: {},
    extraID: 0,
  });
  useEffect(() => {
    if (isNonEmptyVariable(bookingBailsData) && bookingBailsData?.length) {
      const optionsBookingBail = [];
      bookingBailsData?.forEach((element) => {
        if (
          !isNonEmptyVariable(element?.paid) ||
          (isNonEmptyVariable(element?.paid) && !element?.paid) ||
          (isNonEmptyVariable(element?.isPaymentBailAdd) && !isPaymentBailAdd)
        ) {
          optionsBookingBail.push({
            value: element?.bookingBailID || element?.bookingBailUuid,
            label: `$ ${element?.bailAmount}, Bail Type: ${
              isNonEmptyVariable(element?.bailTypeID)
                ? getRefListNameByID("BondType", element?.bailTypeID)
                : ""
            } ${
              isNonEmptyVariable(element?.courtNumber)
                ? "( " + element?.courtNumber + " )"
                : ""
            } `,
            data: element,
          });
        }
      });
      setBookingBails(() => optionsBookingBail);
    }

    if (isNonEmptyVariable(bookingPayments)) {
      let d = [];
      d = bookingPayments?.map((bp) => {
        return {
          ...bp,
          paymentType: getRefListNameByID("Payment_Type", bp?.paymentTypeID),
          fullName: `${isNonEmptyVariable(bp?.bio?.[0]?.fName) ? bp?.bio?.[0]?.fName + " " : ""}${
            isNonEmptyVariable(bp?.bio?.[0]?.mName)
              ? bp?.bio?.[0]?.mName + " "
              : ""
          }${isNonEmptyVariable(bp?.bio?.[0]?.lName) ? bp?.bio?.[0]?.lName : ""}`,
        };
      });

      setPaymentsData(() => d);
    }
  }, [bookingBailsData, bookingPayments]);

  const addPaymentModal = () => {
    setSingleBookingPayments(null);
    setIsBailModal(() => true);
    setisEditVisible(() => false);
  };

  const editPaymentModal = () => {
    setIsBailModal(() => true);
    setisEdit(true);
  };

  const selectRowsFn = (values) => {
    setSingleBookingPayments(values);
    setisEditVisible(() => true);
  };

  const cancelAction = () => {
    setSingleBookingPayments(null);
    setisEditVisible(() => false);
    setIsBailModal(() => false);
    setisEdit(false);
  };

  const onSaveClick = () => {
    paymentForm?.validateFields().then((v) => {
      if (
        (!isNonEmptyVariable(singleBookingPayments) ||
          !singleBookingPayments?.bookingBailPaymentBails?.length) &&
        !fullSearch
      ) {
        showDialog("info", {
          title: "Warning",
          content: "You must select atleast one bail to pay.",
          confirmButtonText: "Ok",
        });
        return;
      }

      // Split BookingBailPaymentsExtra dynamic fields out of the form
      // values — they're saved separately below via /api/extra/save, not
      // part of the main payload (which lists its fields explicitly rather
      // than spreading `v`, so these wouldn't leak into it either way, but
      // pulling them out keeps `v` limited to the payment's own fields for
      // whatever else reads it below).
      const { fieldKeys, typeMap, extraID } = bailPaymentExtraMetaRef.current;
      const bailPaymentExtraSnapshot = {};
      fieldKeys.forEach((k) => {
        const val = v?.[k];
        const htmlType = typeMap[k];
        if (val) {
          bailPaymentExtraSnapshot[k] =
            htmlType === "date" ? dayjs(val).format("MM/DD/YYYY")
            : htmlType === "datetime" ? dayjs(val).format("MM/DD/YYYY HH:mm")
            : val;
        } else {
          bailPaymentExtraSnapshot[k] = null;
        }
        delete v[k];
      });

      if (isNonEmptyVariable(v.paymentPaidDateTime)) {
        v.paymentPaidDateTime =
          v?.paymentPaidDateTime?.format("MM/DD/YYYY HH:mm");
      }

      // Payer info collected on this form (Personal Information section)
      // maps onto the "bio" node the save endpoint now expects, using that
      // node's own field names (getfName/getlName/getmName/etc.) rather than
      // the GET-by-id bio shape (fName/lName/mName) used elsewhere.
      //
      // This is the payer's own bio record, separate from the inmate's -
      // "bring over inmate's name and address" (BailPaymentDetailForm's
      // bringOverInmateNameAndAddress) stashes the inmate's FULL bio here
      // (ssn, race, height, weight, dob, etc. - fields this form has no
      // inputs for) as a starting point, so it survives to the save. What's
      // visible on this form always wins for the fields it does show, since
      // the user may have edited them after bringing the inmate's info over,
      // or typed them by hand without using that shortcut at all.
      //
      // The payer still needs their own bio row though, not the inmate's -
      // so a new payment always creates a new bio (bioId 0); only an edit
      // of an already-saved payment reuses the bioId that payment
      // previously saved.
      // Sending an explicit 0 for a new bio/address doesn't read as "create
      // new" on the backend the way it does for bookingBailPaymentID - it
      // just fails to create the record. So these are left `undefined`
      // instead of 0 when there's no real id yet; JSON.stringify (axios'
      // default request transform) drops undefined keys entirely, meaning
      // the field is omitted from the request rather than sent as 0.
      const payerBioBase = singleBookingPayments?.bio?.[0] || {};
      const payerBioId = payerBioBase?.bioId || undefined;
      const payerBaseAddress = payerBioBase?.bioAddresses?.[0];
      const payerBio = {
        ...payerBioBase,
        bioId: payerBioId,
        fName: v?.fName || "",
        mName: v?.mName || "",
        lName: v?.lName || "",
        suffix: v?.suffix || "",
        sex: v?.sex || "",
        ageAtEntry: v?.age || payerBioBase?.ageAtEntry || null,
        streetNumber: v?.doorNo || "",
        street1: v?.street1 || "",
        street2: v?.street2 || "",
        apartmentNumber: v?.aptNumber || "",
        city: v?.city || "",
        state: v?.stateID || "",
        zip: v?.zip || "",
        bioAddresses: [
          {
            bioAddressId: payerBaseAddress?.bioAddressId || undefined,
            bioId: payerBioId,
            addressId: payerBaseAddress?.addressId || undefined,
            addresses: [
              {
                addressId:
                  payerBaseAddress?.addresses?.[0]?.addressId || undefined,
                addressStreet1: v?.street1 || "",
                addressStreet2: v?.street2 || "",
                addressCity: v?.city || "",
                addressState: v?.stateID || "",
                addressZip: v?.zip || "",
                addressApartmentNumber: v?.aptNumber || "",
                addressStreetNumber: v?.doorNo || "",
              },
            ],
          },
        ],
      };

      // Bails this payment covers only need to travel back as the join-table
      // IDs the endpoint expects, not the full bail record the modal used to
      // pick them (bailAmount/bailType/charge/etc. are UI-only fields).
      const bookingBailPaymentBails = (
        singleBookingPayments?.bookingBailPaymentBails || []
      ).map((bbp) => ({
        bookingBailPaymentBailID: bbp?.bookingBailPaymentBailID || 0,
        bookingBailPaymentID: singleBookingPayments?.bookingBailPaymentID || 0,
        bookingBailID: bbp?.bookingBailID,
      }));

      const payload = {
        bookingBailPaymentID: singleBookingPayments?.bookingBailPaymentID || 0,
        bioID: payerBioId,
        bondCity: v?.bondCity || "",
        bondNumber: singleBookingPayments?.bondNumber || "",
        bondPhone: v?.bondPhone || "",
        bondStateID: v?.bondStateID || null,
        bondStreet1: v?.bondStreet1 || "",
        bondStreet2: singleBookingPayments?.bondStreet2 || "",
        bondZip: v?.bondZip || "",
        bondedBy: v?.bondedBy || "",
        bookingID: bookings?.bookingId,
        checkNumber: v?.checkNumber || "",
        comments: v?.comments || "",
        creatorStaffID: singleBookingPayments?.creatorStaffID || null,
        dateAdded: singleBookingPayments?.dateAdded || null,
        datePaid: v?.paymentPaidDateTime?.split(" ")?.[0],
        timeAdded: singleBookingPayments?.timeAdded || null,
        timePaid: v?.paymentPaidDateTime?.split(" ")?.[1],
        paymentAmount: v?.paymentAmount,
        paymentTypeID: v?.paymentTypeID,
        receiptNumber: v?.receiptNumber,
        bio: [payerBio],
        bookingBailPaymentBails,

        // UI-only fields, not part of the save contract, kept for the
        // "List of Bail Payments" table and the fullSearch optimistic list.
        bailPaymentsUuid: singleBookingPayments?.bailPaymentsUuid || uuidV4(),
        fullName: `${
          isNonEmptyVariable(bookings?.bio?.[0]?.fName)
            ? bookings?.bio?.[0]?.fName + " "
            : ""
        }${
          isNonEmptyVariable(bookings?.bio?.[0]?.mName)
            ? bookings?.bio?.[0]?.mName + " "
            : ""
        }${
          isNonEmptyVariable(bookings?.bio?.[0]?.lName)
            ? bookings?.bio?.[0]?.lName
            : ""
        }`,
      };

      let b = bookingBailsData;
      singleBookingPayments?.bookingBailPaymentBails?.forEach((bbpb) => {
        b = b?.map((bb) => {
          if (
            bb?.bookingBailID === bbpb?.bookingBailID ||
            bb?.bookingBailUuid === bbpb?.bookingBailUuid
          ) {
            return {
              ...bb,
              paid: true,
            };
          }
          return bb;
        });
      });

      let url = "";
      if (fullSearch) {
        const prevBookings = bookings; // <-- from useSelector
        const updated = {
          ...prevBookings,
          bookingBailPayments: [
            ...(prevBookings?.bookingBailPayments || []),
            payload,
          ],
        };
        dispatch(setBookings(updated));
        cancelAction();
        return;
      }

      if (isEditVisible) {
        url = API_ENDPOINTS.bookingBailPayment_update;
      } else {
        url = API_ENDPOINTS.bookingBailPayment_create;
      }

      // Captured before the save so we can tell, once the booking is
      // refetched below, which payment (if any) the save just created —
      // an update already has bookingBailPaymentID directly from
      // singleBookingPayments, but a brand new payment only gets a real id
      // once refetched, and there's no other way to tell which of the
      // refetched payments is the new one.
      const previousPaymentIds = new Set(
        (bookingPayments || [])
          .map((p) => p?.bookingBailPaymentID)
          .filter(Boolean),
      );

      setisAPICall(() => true);
      api
        .post(url, payload)
        .then(async (res) => {
          if (res?.data?.statusCode === 200) {
            const data = await getBookingsByNumberUtil(bookings?.bookingNumber);
            dispatch(setLastBookingsFromAPI(data));
            dispatch(
              setBookings({
                ...data,
              }),
            );

            // Resolve the real bookingBailPaymentID and save
            // BookingBailPaymentsExtra.
            if (Object.keys(bailPaymentExtraSnapshot).length > 0) {
              const resolvedPaymentID =
                singleBookingPayments?.bookingBailPaymentID ??
                (data?.bookingBailPayments || []).find(
                  (p) =>
                    p?.bookingBailPaymentID &&
                    !previousPaymentIds.has(p.bookingBailPaymentID),
                )?.bookingBailPaymentID;

              if (resolvedPaymentID) {
                saveExtraTableRecord({
                  tableName: "BookingBailPaymentsExtra",
                  idColumn: "bookingBailPaymentID",
                  idValue: resolvedPaymentID,
                  extraRecord: {
                    ...bailPaymentExtraSnapshot,
                    bookingBailPaymentsExtraID: extraID ?? 0,
                  },
                  extraIdKey: "bookingBailPaymentsExtraID",
                }).catch((error) => {
                  console.error(
                    "[ChargesBailPaymentsDetails] BookingBailPaymentsExtra save failed:",
                    error,
                  );
                });
              }
            }
            if (isNonEmptyVariable(isEditVisible)) {
              notificationService.success(
                "Success!",
                "Bail Payment updated successfully",
              );
            } else {
              notificationService.success(
                "Success!",
                "Bail Payment created successfully",
              );
            }

            dispatch(
              setBookings({
                bookingBails: b,
              }),
            );

            cancelAction();
          } else {
            if (isNonEmptyVariable(isEditVisible)) {
              notificationService.error(
                "Failed!",
                "Bail Payment updated failed",
              );
            } else {
              notificationService.error(
                "Failed!",
                "Bail Payment created failed",
              );
            }
          }
        })
        .catch(() => {
          if (isNonEmptyVariable(isEditVisible)) {
            notificationService.error("Failed!", "Bail Payment updated failed");
          } else {
            notificationService.error("Failed!", "Bail Payment created failed");
          }
        })
        .finally(() => {
          setisAPICall(() => false);
        });

      // if (isEditVisible) {
      //   updateBookingPayments = bookingPayments?.map((bp) => {
      //     if (
      //       bp?.bailPaymentsUuid === singleBookingPayments?.bailPaymentsUuid
      //     ) {
      //       return {
      //         ...bp,
      //         ...singleBookingPayments,
      //         ...v,
      //         datePaid: v?.paymentPaidDateTime?.[0],
      //         timePaid: v?.paymentPaidDateTime?.[1],
      //         bioID: bookings?.bio?.[0]?.bioId || null,
      //         fullName: `${
      //           bookings?.bio?.[0]?.fName ||
      //           "" + " " + bookings?.bio?.[0]?.mName ||
      //           "" + " " + bookings?.bio?.[0]?.lName ||
      //           ""
      //         }`,
      //       };
      //     }
      //     return bp;
      //   });
      // } else {
      //   const data = {
      //     ...singleBookingPayments,
      //     ...v,
      //     datePaid: v?.paymentPaidDateTime?.[0],
      //     timePaid: v?.paymentPaidDateTime?.[1],
      //     bailPaymentsUuid: uuidV4(),
      //     bioID: bookings?.bio?.[0]?.bioId || null,
      //     fullName: `${
      //       bookings?.bio?.[0]?.fName ||
      //       "" + " " + bookings?.bio?.[0]?.mName ||
      //       "" + " " + bookings?.bio?.[0]?.lName ||
      //       ""
      //     }`,
      //   };
      //   updateBookingPayments.push(data);
      // }

      // dispatch(
      //   setBookings({
      //     bookingBailPayments: updateBookingPayments,
      //     bookingBails: b,
      //   })
      // );
    });
  };

  const deletePayment = () => {
    let data = _.cloneDeep(paymentsData);
    data = data?.filter((d) => {
      return d?.bailPaymentsUuid !== singleBookingPayments?.bailPaymentsUuid;
    });

    dispatch(
      setBookings({
        bookingBailPayments: data,
      }),
    );

    if (isNonEmptyVariable(singleBookingPayments?.bookingBailPaymentID)) {
      const url =
        API_ENDPOINTS.bookingBailPayments +
        "/id/" +
        singleBookingPayments?.bookingBailPaymentID;
      dispatch(setDeleteAPis(url));
    }

    cancelAction();
  };
  useEffect(() => {
    if (isEdit) {
      let showEdit = hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.MODIFY,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report,
      );
      if (showEdit) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    } else {
      setIsDisabled(false);
    }
  }, [isEdit]);
  return (
    <>
      <InmateDetailsTableContainer>
        <TableHeader>
          <h6>List of Bail Payments</h6>
          <StyledFlex className="right" gap="10px">
            {startingPoint != "FolderSearch" && (
              <Button
                onClick={addPaymentModal}
                disabled={
                  !bookingsFieldsEnabled ||
                  isConfiguring ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.ADD,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    SUB_MODULES_LIST_WITH_ID.Arrest_Report,
                  )
                }
              >
                <PlusOutlined /> Add
              </Button>
            )}
            {
              <Button
                onClick={editPaymentModal}
                disabled={
                  !isEditVisible ||
                  isConfiguring ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.VIEW,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    SUB_MODULES_LIST_WITH_ID.Arrest_Report,
                  )
                }
              >
                Details
              </Button>
            }
            {startingPoint != "FolderSearch" && (
              <Popconfirm
                title="Delete the bail payment"
                description="Are you sure to delete this bail payment?"
                onConfirm={deletePayment}
                onCancel={() => {}}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  style={{ marginLeft: "10px" }}
                  color="primary"
                  type="primary"
                  disabled={
                    !isEditVisible ||
                    !bookingsFieldsEnabled ||
                    isConfiguring ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.subModule,
                      LABELS_CAPITAL.DELETE,
                      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                      SUB_MODULES_LIST_WITH_ID.Arrest_Report,
                    )
                  }
                >
                  DELETE
                </Button>
              </Popconfirm>
            )}
            <Button onClick={cancelAction} disabled={!isEditVisible || isConfiguring}>
              Cancel
            </Button>

            {/* Config — drag-reorder/resize columns; only while Modify is active */}
            {!isConfiguring ? (
              <Button
                htmlType="button"
                onClick={startConfiguring}
              >
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Do you want to save the current list view configuration?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
              >
                <Button htmlType="button">
                  {configButtonLabel}
                </Button>
              </Popconfirm>
            )}
          </StyledFlex>
        </TableHeader>
        {hasCheckSecurity(
          Security_Types.functionality,
          Entity_types.subModule,
          LABELS_CAPITAL.VIEW,
          MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
          SUB_MODULES_LIST_WITH_ID.Arrest_Report,
        ) && (
          <Table
            ref={dataTableRef}
            headers={bailPaymentsHeaders}
            draggable={isConfiguring}
            data={paymentsData}
            onRowClick={selectRowsFn}
            onRowDoubleClick={(value) => {
              selectRowsFn(value);
              editPaymentModal();
            }}
            selected={singleBookingPayments === null}
          />
        )}
      </InmateDetailsTableContainer>

      {isBailModal && (
        <CustomModal
          header="Bail Payment Details"
          size="xl"
          show={isBailModal}
          onHide={cancelAction}
          onSaveClick={onSaveClick}
          isAPICall={isAPICall}
          isDisabled={isDisabled}
          onCloseClick={() => setisEdit(false)}
        >
          <BailPaymentDetailForm
            chargesData={chargesData}
            bookingBails={bookingBails}
            singleBookingPayments={singleBookingPayments}
            paymentForm={paymentForm}
            setSingleBookingPayments={setSingleBookingPayments}
            startingPoint={startingPoint}
            onExtraMetaChange={(meta) => {
              bailPaymentExtraMetaRef.current = meta;
            }}
          />
        </CustomModal>
      )}
    </>
  );
};
export default ChargesBailPaymentsDetails;
